import { useState, type ReactNode } from "react";

import { makePostApiCall, makePutApiCall } from "../../../../shared/makeApiCall";
import { useToast } from "../../../../contexts/ToastContext";

import ModelErrorComponent from "../../../../components/modals/ModelErrorComponent";
import type { UserFormValues } from "../../../../components/forms/user/UserDetailsForm";
import type { ApiMessage } from "../../../../types/api";
import type { ResetUserPasswordPayload, UserEntity, UserRecord } from "../../../../types/users";

interface ModalState { open: boolean; childComponent: ReactNode }

export function UserDetailsViewModel(logout: () => void, userId: string) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<ModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<UserRecord | null>(null);

  const showErrorModal = (title: string, subtitle: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getUser = async function () {
    setLoading(true);
    const callResult = await makePostApiCall<UserRecord>(`users/${userId}`, {});
    if (callResult.success) {
      setUser(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not load user", callResult.data.message || "An error occurred while fetching the user");
    }
    setLoading(false);
  };

  const createUser = async function (values: UserFormValues) {
    const callResult = await makePostApiCall<UserEntity, UserFormValues>(`createUser`, values);

    if (callResult.success) {
      showSuccess("User created");
      window.location.assign("/users/" + callResult.data.id);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not create user", callResult.data.message || "An error occurred while creating the user");
    }
  };

  const updateUser = async function (values: UserFormValues) {
    const callResult = await makePutApiCall<UserEntity, UserFormValues>(`users/${userId}`, values);

    if (callResult.success) {
      showSuccess("User updated");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not update user", callResult.data.message ?? "An error occurred while updating the user");
    }
  };

  const changeUserPasswordAdmin = async function (
    targetUserId: number,
    payload: ResetUserPasswordPayload,
  ) {
    // targetUserId: id of the user whose password will be changed
    // payload expected: { password, password_confirmation }

    setLoading(true);
    const callResult = await makePostApiCall<ApiMessage, ResetUserPasswordPayload>(`/users/reset-password/${targetUserId}`, payload);
    setLoading(false);
    if (callResult.success) {
      showSuccess(callResult.data.message || "Action completed");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not change password", callResult.data.message || "An error occurred while changing the password");
    }
  };

  return {
    createUser,
    updateUser,
    changeUserPasswordAdmin,
    getUser,
    user,
    loading,
    emptyModal,
    setEmptyModelOpen,
  };
}
